import { EventVerificationConfig } from '../../../utils/eventVerification';
import { Box, Card, FormControlLabel, Switch, TextField, Typography } from '../../ui';
import SectionLabel from './SectionLabel';

/**
 * Attendance verification card — External events only. How the LMS
 * confirms the learner actually attended. Multiple methods can be
 * enabled; any one satisfied marks completion (OR-logic). Two methods
 * active today, two disabled placeholders for upcoming backend work.
 *
 * Controlled: the full config lives in the parent form so it persists
 * with the rest of the course settings.
 */
export default function CourseVerificationSection({
  verification,
  onChange,
}: {
  verification: EventVerificationConfig;
  onChange: (next: EventVerificationConfig) => void;
}) {
  return (
    <Card sx={{ p: 3 }}>
      <SectionLabel
        title="Attendance verification"
        subtitle="Pick how learners prove they attended. You can enable more than one — any satisfied method marks the event complete."
      />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ── Confirmation code ── */}
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: verification.codeEnabled ? 'primary.main' : 'divider',
            bgcolor: verification.codeEnabled ? 'rgba(var(--brand-rgb), 0.06)' : 'background.paper',
            transition: 'border-color 150ms ease, background-color 150ms ease',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={verification.codeEnabled}
                onChange={(e) => onChange({ ...verification, codeEnabled: e.target.checked })}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  Confirmation code
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Share a code during the event (slide, chat, verbal). Learners enter it on the event page to mark complete.
                </Typography>
              </Box>
            }
            sx={{ ml: 0, alignItems: 'flex-start', '& .MuiSwitch-root': { mt: 0.25 } }}
          />
          {verification.codeEnabled && (
            <TextField
              label="Code"
              value={verification.code}
              onChange={(e) => onChange({ ...verification, code: e.target.value })}
              placeholder="e.g. SMILE2026"
              fullWidth
              size="small"
              sx={{ mt: 1.5 }}
              helperText="Case-insensitive. Reveal this near the end of the event so the audience can't share it before completion."
              inputProps={{ autoCapitalize: 'characters', spellCheck: false }}
            />
          )}
        </Box>

        {/* ── Post-event attestation ── */}
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: verification.attestationEnabled ? 'primary.main' : 'divider',
            bgcolor: verification.attestationEnabled ? 'rgba(var(--brand-rgb), 0.06)' : 'background.paper',
            transition: 'border-color 150ms ease, background-color 150ms ease',
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={verification.attestationEnabled}
                onChange={(e) => onChange({ ...verification, attestationEnabled: e.target.checked })}
              />
            }
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  Post-event attestation
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Learner ticks a checkbox affirming they attended. Customize the statement they sign off on below.
                </Typography>
              </Box>
            }
            sx={{ ml: 0, alignItems: 'flex-start', '& .MuiSwitch-root': { mt: 0.25 } }}
          />
          {verification.attestationEnabled && (
            <TextField
              label="Attestation statement"
              value={verification.attestationText}
              onChange={(e) => onChange({ ...verification, attestationText: e.target.value })}
              placeholder="I attended this event in full and engaged with the material."
              fullWidth
              multiline
              minRows={2}
              size="small"
              sx={{ mt: 1.5 }}
              helperText="Empty falls back to a generic statement on the learner page."
            />
          )}
        </Box>

        {/* ── Stubs: admin-marked attendance + webinar API ──
            Rendered disabled so admins see the surface coming.
            Toggles are inert until backend work lands. */}
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            opacity: 0.75,
          }}
        >
          <FormControlLabel
            control={<Switch checked={false} disabled />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  Admin-marked attendance{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    · Coming soon
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  After the event, open an attendance panel and tick attendees (or paste a CSV from Zoom / Teams).
                </Typography>
              </Box>
            }
            sx={{ ml: 0, alignItems: 'flex-start', '& .MuiSwitch-root': { mt: 0.25 } }}
          />
        </Box>
        <Box
          sx={{
            p: 2,
            borderRadius: 1.5,
            border: '1px dashed',
            borderColor: 'divider',
            bgcolor: 'grey.50',
            opacity: 0.75,
          }}
        >
          <FormControlLabel
            control={<Switch checked={false} disabled />}
            label={
              <Box>
                <Typography variant="body2" fontWeight={700}>
                  Webinar platform integration{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    · Coming soon
                  </Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Pull attendance directly from Zoom, GoToWebinar, or Teams. Requires connecting your provider.
                </Typography>
              </Box>
            }
            sx={{ ml: 0, alignItems: 'flex-start', '& .MuiSwitch-root': { mt: 0.25 } }}
          />
        </Box>
      </Box>
    </Card>
  );
}
